import {
  Button,
  Chip,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import React, { useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import { useMemberActions } from '../../hooks';
import type { DirectoryMember, MemberAssignment } from '../../types';
import { locationLabel, memberName, tierStyle } from '../../utils';

export interface EditAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  /** The member being edited; absent while the modal is closed. */
  member: DirectoryMember | undefined;
  /** Opens the role picker for this member. */
  onAddAssignment: (userId: string) => void;
  onChanged: () => Promise<void>;
}

const assignmentKey = (assignment: MemberAssignment): string =>
  `${assignment.locationId}-${assignment.role.id}`;

export const EditAssignmentsModal: React.FC<EditAssignmentsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  member,
  onAddAssignment,
  onChanged,
}) => {
  const { removeRole, revokeAll, isWorking, error, clearError } =
    useMemberActions(onChanged);

  // Held while the service is asking whether losing the last assignment — and
  // so the workspace — is intended.
  const [lastOne, setLastOne] = useState<MemberAssignment | null>(null);

  const close = () => {
    clearError();
    setLastOne(null);
    onClose();
  };

  const drop = async (assignment: MemberAssignment) => {
    const outcome = await removeRole({
      workspaceId,
      userId: member?.userId ?? '',
      locationId: assignment.locationId,
      roleId: assignment.role.id,
    });

    if (outcome.wouldRemoveFromWorkspace) {
      setLastOne(assignment);
    }
  };

  const confirmLastOne = async () => {
    if (!lastOne || !member) {
      return;
    }

    const outcome = await removeRole({
      workspaceId,
      userId: member.userId,
      locationId: lastOne.locationId,
      roleId: lastOne.role.id,
      allowRemoveFromWorkspace: true,
    });

    setLastOne(null);

    if (outcome.removed) {
      close();
    }
  };

  const who = memberName(member?.name ?? null);

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Edit assignments
          <span className="text-small font-normal text-default-500">
            {who} — every place they hold a role, and what they can do there.
          </span>
        </ModalHeader>
        <Divider />
        <ModalBody className="gap-3 py-4">
          {lastOne ? (
            <div className="space-y-3 rounded-medium bg-danger-50 p-4">
              <p className="text-small text-danger">
                That is the only assignment {who} holds, so removing it takes
                them out of the workspace.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  color="danger"
                  onPress={confirmLastOne}
                  isLoading={isWorking}
                  isDisabled={isWorking}
                >
                  Remove anyway
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onPress={() => setLastOne(null)}
                  isDisabled={isWorking}
                >
                  Keep it
                </Button>
              </div>
            </div>
          ) : null}

          {(member?.assignments ?? []).map((assignment) => {
            const tier = tierStyle(assignment.hierarchyType);

            return (
              <div
                key={assignmentKey(assignment)}
                className="flex items-center justify-between gap-3 rounded-medium border border-default-200 px-3 py-2.5"
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
                    aria-hidden
                  />
                  <span className="text-small text-default-500">
                    {assignment.parentName && `${assignment.parentName} · `}
                    {locationLabel(
                      assignment.locationName,
                      assignment.locationId
                    )}
                  </span>
                  <Chip size="sm" variant="flat" className="font-medium">
                    {assignment.role.displayName}
                  </Chip>
                </span>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isDisabled={isWorking}
                  onPress={() => drop(assignment)}
                >
                  Remove
                </Button>
              </div>
            );
          })}

          <Button
            size="sm"
            variant="flat"
            startContent={<FiPlus />}
            isDisabled={isWorking}
            onPress={() => {
              if (member) {
                onAddAssignment(member.userId);
                close();
              }
            }}
            className="self-start"
          >
            Add an assignment
          </Button>

          {error && (
            <div className="rounded-medium bg-danger-50 p-3 text-small text-danger">
              {error}
            </div>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter className="justify-between">
          <Button
            variant="light"
            color="danger"
            isDisabled={isWorking}
            onPress={async () => {
              if (!member) {
                return;
              }
              const revoked = await revokeAll({
                workspaceId,
                userId: member.userId,
              });
              if (revoked) {
                close();
              }
            }}
          >
            Remove from workspace
          </Button>
          <Button variant="bordered" onPress={close} isDisabled={isWorking}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
